import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';

export default function ExpenseCategoryIndex() {
    const { t } = useContext(TranslatorContext);

    const [parentCategories, setParentCategories] = useState([]);
    const [childCategories, setChildCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedParent, setSelectedParent] = useState(null);
    const [currentId, setCurrentId] = useState(null);
    const [currentType, setCurrentType] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Add Parent Modal States
    const [addParentModal, setAddParentModal] = useState(false);
    const [addParentForm, setAddParentForm] = useState({ name: '' });
    const [addParentErrors, setAddParentErrors] = useState({});
    const [addParentLoading, setAddParentLoading] = useState(false);

    // Add Child Modal States
    const [addChildModal, setAddChildModal] = useState(false);
    const [addChildForm, setAddChildForm] = useState({ name: '' });
    const [addChildErrors, setAddChildErrors] = useState({});
    const [addChildLoading, setAddChildLoading] = useState(false);

    // Update Parent Modal States
    const [updateParentModal, setUpdateParentModal] = useState(false);
    const [updateParentForm, setUpdateParentForm] = useState({ id: null, name: '' });
    const [updateParentErrors, setUpdateParentErrors] = useState({});
    const [updateParentLoading, setUpdateParentLoading] = useState(false);

    // Update Child Modal States
    const [updateChildModal, setUpdateChildModal] = useState(false);
    const [updateChildForm, setUpdateChildForm] = useState({ id: null, name: '' });
    const [updateChildErrors, setUpdateChildErrors] = useState({});
    const [updateChildLoading, setUpdateChildLoading] = useState(false);

    useEffect(() => {
        fetchExpenseCategories();
    }, []);

    useEffect(() => {
        if (selectedParent) {
            loadChildCategories(selectedParent.id);
        }
    }, [selectedParent]);

    const fetchExpenseCategories = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/expense-categories`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                const allCategories = res.data.expenseCategories || [];
                const parents = allCategories.filter(item => !item.parent_id);
                setParentCategories(parents);

                if (parents.length > 0 && !selectedParent) {
                    setSelectedParent(parents[0]);
                } else if (selectedParent) {
                    const updatedParent = parents.find(p => p.id === selectedParent.id);
                    if (updatedParent) {
                        setSelectedParent(updatedParent);
                    }
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const loadChildCategories = (parentId) => {
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/expense-categories`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            const allCategories = res.data.expenseCategories || [];
            const children = allCategories.filter(item => item.parent_id === parentId);
            setChildCategories(children);
        }).catch(err => console.error(err));
    };

    // Add Parent Modal Functions
    const openAddParentModal = () => {
        setAddParentForm({ name: '' });
        setAddParentErrors({});
        setAddParentModal(true);
    };

    const handleAddParentChange = (e) => {
        const { name, value } = e.target;
        setAddParentForm(prev => ({ ...prev, [name]: value }));
        if (addParentErrors[name]) {
            setAddParentErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleAddParentSubmit = async (e) => {
        e.preventDefault();
        setAddParentLoading(true);
        setAddParentErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/expense-categories`,
                { name: addParentForm.name, parent_id: null },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.expenseCategories) {
                fetchExpenseCategories();
                setAddParentModal(false);
                setAddParentForm({ name: '' });
                
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Expense category created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setAddParentErrors(error.response.data.errors || {});
                
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating expense category:', error);
                
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create expense category'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setAddParentLoading(false);
        }
    };

    // Add Child Modal Functions
    const openAddChildModal = () => {
        if (!selectedParent) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please select a category first'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }
        setAddChildForm({ name: '' });
        setAddChildErrors({});
        setAddChildModal(true);
    };

    const handleAddChildChange = (e) => {
        const { name, value } = e.target;
        setAddChildForm(prev => ({ ...prev, [name]: value }));
        if (addChildErrors[name]) {
            setAddChildErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleAddChildSubmit = async (e) => {
        e.preventDefault();
        setAddChildLoading(true);
        setAddChildErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/expense-categories`,
                {
                    name: addChildForm.name,
                    parent_id: selectedParent.id
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.expenseCategories) {
                fetchExpenseCategories();
                setAddChildModal(false);
                setAddChildForm({ name: '' });
                
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Expense sub-category created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setAddChildErrors(error.response.data.errors || {});
                
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating expense sub-category:', error);
                
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create expense sub-category'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setAddChildLoading(false);
        }
    };

    // Update Parent Modal Functions
    const openUpdateParentModal = (parent) => {
        setUpdateParentForm({
            id: parent.id,
            name: parent.name
        });
        setUpdateParentErrors({});
        setUpdateParentModal(true);
    };

    const handleUpdateParentChange = (e) => {
        const { name, value } = e.target;
        setUpdateParentForm(prev => ({ ...prev, [name]: value }));
        if (updateParentErrors[name]) {
            setUpdateParentErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateParentSubmit = async (e) => {
        e.preventDefault();
        setUpdateParentLoading(true);
        setUpdateParentErrors({});

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/expense-categories/${updateParentForm.id}`,
                {
                    name: updateParentForm.name,
                    parent_id: null
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.expenseCategory) {
                fetchExpenseCategories();
                setUpdateParentModal(false);
                
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Expense category updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateParentErrors(error.response.data.errors || {});
                
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateParentErrors({ general: [error.response.data.error] });
                
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.error || t('Category not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating expense category:', error);
                
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update expense category'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateParentLoading(false);
        }
    };

    // Update Child Modal Functions
    const openUpdateChildModal = (child) => {
        setUpdateChildForm({
            id: child.id,
            name: child.name
        });
        setUpdateChildErrors({});
        setUpdateChildModal(true);
    };

    const handleUpdateChildChange = (e) => {
        const { name, value } = e.target;
        setUpdateChildForm(prev => ({ ...prev, [name]: value }));
        if (updateChildErrors[name]) {
            setUpdateChildErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateChildSubmit = async (e) => {
        e.preventDefault();
        setUpdateChildLoading(true);
        setUpdateChildErrors({});

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/expense-categories/${updateChildForm.id}`,
                {
                    name: updateChildForm.name,
                    parent_id: selectedParent?.id
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.expenseCategory) {
                fetchExpenseCategories();
                setUpdateChildModal(false);
                
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Expense sub-category updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateChildErrors(error.response.data.errors || {});
                
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateChildErrors({ general: [error.response.data.error] });
                
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.error || t('Sub-category not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating expense sub-category:', error);
                
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update expense sub-category'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateChildLoading(false);
        }
    };

    // Delete Function
    const handleDelete = async () => {
        setDeleteLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/expense-categories/${currentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchExpenseCategories();
            
            Swal.fire({
                icon: 'success',
                title: t('deleted'),
                text: currentType === 'parent' 
                    ? t('Expense category deleted successfully')
                    : t('Expense sub-category deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting expense category:', error);
            
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setDeleteLoading(false);
            setCurrentId(null);
            setCurrentType(null);
        }
    };

    // Delete confirmation with SweetAlert
    const confirmDelete = (id, type) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: type === 'parent' 
                ? t("Want to delete this expense category?")
                : t("Want to delete this expense sub-category?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                setCurrentId(id);
                setCurrentType(type);
                handleDelete();
            }
        });
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 style={{ color: "#777" }}>Expense Categories</h4>
                <ButtonComponent
                    className="btn"
                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                    onClick={openAddParentModal}
                >
                    + Add Category
                </ButtonComponent>
            </div>

            <div className="card-container">
                <Row className="g-0">
                    {/* LEFT COLUMN: Parent Categories */}
                    <Col md={4} className="border-end">
                        <div className="header-strip">
                            <span>
                                <i className="material-icons align-middle me-2" style={{ fontSize: '18px' }}>category</i>
                                Expense Categories
                            </span>
                        </div>
                        <div style={{ height: "70vh", overflowY: "auto" }}>
                            {parentCategories.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    No categories found
                                </div>
                            ) : (
                                parentCategories.map((parent) => (
                                    <div
                                        key={parent.id}
                                        className={`sidebar-item ${selectedParent?.id === parent.id ? 'active' : ''}`}
                                        onClick={() => setSelectedParent(parent)}
                                    >
                                        <i
                                            className="material-icons delete-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDelete(parent.id, 'parent');
                                            }}
                                        >delete</i>
                                        <i
                                            className="material-icons edit-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openUpdateParentModal(parent);
                                            }}
                                        >edit</i>
                                        <span className={selectedParent?.id === parent.id ? 'text-primary' : ''}>
                                            {parent.name}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </Col>

                    {/* RIGHT COLUMN: Child Categories */}
                    <Col md={8} className="p-4">
                        <div className="d-flex justify-content-end mb-3">
                            <ButtonComponent
                                className="btn btn-sm"
                                style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                onClick={openAddChildModal}
                                disabled={!selectedParent}
                            >
                                + Add Sub-Category
                            </ButtonComponent>
                        </div>

                        {!selectedParent ? (
                            <div className="text-center text-muted py-5">
                                <i className="material-icons" style={{ fontSize: '48px' }}>category</i>
                                <p className="mt-2">Select a category from the left to view its sub-categories</p>
                            </div>
                        ) : childCategories.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                                <p className="mt-2">No sub-categories found for this category</p>
                                <ButtonComponent
                                    className="btn btn-sm mt-2"
                                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                    onClick={openAddChildModal}
                                >
                                    + Add First Sub-Category
                                </ButtonComponent>
                            </div>
                        ) : (
                            <Table responsive hover borderless>
                                <thead>
                                    <tr className="table-header">
                                        <th>Sub-Category Name</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {childCategories.map((child) => (
                                        <tr key={child.id} className="table-row">
                                            <td className="align-middle">
                                                <i
                                                    className="material-icons delete-icon"
                                                    onClick={() => confirmDelete(child.id, 'child')}
                                                >delete</i>
                                                {child.name}
                                            </td>
                                            <td className="text-end align-middle">
                                                <button
                                                    className="action-btn"
                                                    onClick={() => openUpdateChildModal(child)}
                                                >
                                                    <i className="material-icons" style={{ fontSize: "16px" }}>edit_note</i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Col>
                </Row>
            </div>

            {/* Add Parent Modal */}
            <Modal
                show={addParentModal}
                onHide={() => setAddParentModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Add Expense Category
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddParentSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={addParentForm.name}
                                onChange={handleAddParentChange}
                                isInvalid={!!addParentErrors.name}
                                placeholder="Enter category name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {addParentErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setAddParentModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={addParentLoading}
                        >
                            {addParentLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Child Modal */}
            <Modal
                show={addChildModal}
                onHide={() => setAddChildModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Add Expense Sub-Category
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddChildSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {selectedParent && (
                            <div className="alert alert-info mb-3">
                                <i className="material-icons me-2" style={{ fontSize: '16px' }}>info</i>
                                Adding sub-category under: <strong>{selectedParent.name}</strong>
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={addChildForm.name}
                                onChange={handleAddChildChange}
                                isInvalid={!!addChildErrors.name}
                                placeholder="Enter sub-category name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {addChildErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setAddChildModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={addChildLoading}
                        >
                            {addChildLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Parent Modal */}
            <Modal
                show={updateParentModal}
                onHide={() => setUpdateParentModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit Expense Category
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateParentSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateParentErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateParentErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateParentForm.name}
                                onChange={handleUpdateParentChange}
                                isInvalid={!!updateParentErrors.name}
                                placeholder="Enter category name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateParentErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateParentModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateParentLoading}
                        >
                            {updateParentLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Child Modal */}
            <Modal
                show={updateChildModal}
                onHide={() => setUpdateChildModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit Expense Sub-Category
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateChildSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateChildErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateChildErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateChildForm.name}
                                onChange={handleUpdateChildChange}
                                isInvalid={!!updateChildErrors.name}
                                placeholder="Enter sub-category name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateChildErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateChildModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateChildLoading}
                        >
                            {updateChildLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>


        </PageLayout>
    );
}